import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import { useFacebookPostData } from '#src/app/pages/psf/shared/hooks';
import type { UseFacebookPostDataResult } from '#src/app/pages/psf/shared/hooks';
import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';

import type { FacebookPostSheetTreatOutcome } from './firedFacebookPostSheet';

type FacebookPostSheetEligibilityReason =
	| 'eligible'
	| 'locale'
	| 'country'
	| 'pending'
	| 'no_caption'
	| 'no_groups'
	| 'fetch_error';

const NOT_READY_REASONS = {
	'no-caption': 'no_caption',
	'no-groups': 'no_groups',
	'fetch-error': 'fetch_error',
} as const satisfies Record<
	Extract<UseFacebookPostDataResult, { status: 'not-ready' }>['reason'],
	FacebookPostSheetEligibilityReason
>;

function getEligibilityReason(
	postData: UseFacebookPostDataResult,
	isEnglishLocale: boolean,
	isUsCountry: boolean,
): FacebookPostSheetEligibilityReason {
	if (!isEnglishLocale) return 'locale';
	if (!isUsCountry) return 'country';
	if (postData.status === 'pending') return 'pending';
	if (postData.status === 'not-ready') return NOT_READY_REASONS[postData.reason];
	return 'eligible';
}

const NO_GROUPS: readonly FacebookGroupRecommendation[] = [];

export type UseFacebookPostSheetEligibilityResult = {
	/**
	 * The arm-independent eligibility: kill switch on, English locale, US country, and caption and one or
	 * more groups ready. `useTreatFacebookPostSheetExperiment` adds the experiment-side clauses on top.
	 */
	isEligible: boolean;
	/** The cleaned caption once the fetch resolves ready, else `null`. The post sheet previews and copies it. */
	caption: string | null;
	/** The group recommendations once the fetch resolves ready, else empty. The `variant_2` group cards list them. */
	groups: readonly FacebookGroupRecommendation[];
	startFetch: () => void;
	/**
	 * Fires the probe for one Facebook tap, from the grid icon or the channel-aware CTA button, with how
	 * that tap's treat ended. `useFacebookPostSheetInterception` calls it once per tap.
	 */
	reportTap: (treatOutcome: FacebookPostSheetTreatOutcome) => void;
};

/**
 * Facebook post sheet eligibility gate + coverage probe (CHANGE-99694). While the kill switch is on, the
 * probe records, on every Facebook tap, whether the supporter is eligible and why not, from
 * arm-independent inputs only, so it sizes the population before any arm is live. Since CHANGE-99697 it
 * also carries the tap's treat outcome.
 */
export function useFacebookPostSheetEligibility(petitionSlug: string): UseFacebookPostSheetEligibilityResult {
	const {
		data: { facebookPostSheetFcm },
	} = usePsfPromoteOrSharePageData();
	const killSwitchOn = !!facebookPostSheetFcm?.enabled;

	const isEnglishLocale = useLocale().startsWith('en');
	const isUsCountry = useCountryCode() === 'US';
	// Only English-locale US users can ever be eligible, so nobody else pays for the fetch. The experiment
	// config's `enabledIf` repeats this rule, so change both if the market widens.
	const canFetch = killSwitchOn && isEnglishLocale && isUsCountry;

	const [shouldFetch, setShouldFetch] = useState(false);
	const startFetch = useCallback(() => {
		if (canFetch) setShouldFetch(true);
	}, [canFetch]);

	const postData = useFacebookPostData(petitionSlug, shouldFetch && canFetch);

	const reason = getEligibilityReason(postData, isEnglishLocale, isUsCountry);
	const isEligible = killSwitchOn && reason === 'eligible';
	const recommendationsStatus = postData.status === 'pending' ? null : postData.recommendationsStatus;

	const track = useTracking();
	const reportTap = useCallback(
		(treatOutcome: FacebookPostSheetTreatOutcome) => {
			// With the kill switch off, nobody can be in the experiment, so the probe stays silent.
			if (!killSwitchOn) return;
			void track('combo_psf_share_slide_up_facebook_post_sheet_eligibility', {
				facebook_post_sheet_eligible: isEligible,
				...(isEligible ? {} : { reason }),
				facebook_post_sheet_recommendations_status: recommendationsStatus,
				facebook_post_sheet_treat_outcome: treatOutcome,
			});
		},
		[track, killSwitchOn, isEligible, reason, recommendationsStatus],
	);

	const caption = postData.status === 'ready' ? postData.caption : null;
	const groups = postData.status === 'ready' ? postData.groups : NO_GROUPS;

	return { isEligible, caption, groups, startFetch, reportTap };
}
